import { __spreadArray, __assign, __extends } from 'tslib';
import { removeVNodeDom, createCommentVNode, createTextVNode, createComponentVNode, isComponentClass, validateProps, inject, provide, setInstance, findDomFromVNode, Component as Component$1, callAll } from 'intact';
export * from 'intact';
import { version, createElement, createContext, cloneElement, forwardRef, Fragment, createRef } from 'react';
import { isString, isNumber, noop, isArray, isInvalid, isStringOrNumber, isNullOrUndefined, isObject, isFunction } from 'intact-shared';
import { render, unstable_batchedUpdates, unstable_renderSubtreeIntoContainer } from 'react-dom';

var FakePromise = /*#__PURE__*/ /*#__PURE__*/ /** @class */ (function () {
    function FakePromise(executor) {
        this.resolved = false;
        this.callbacks = [];
        executor(this.resolve.bind(this));
    }
    FakePromise.all = function (promises) {
        if (promises.all)
            return promises.all;
        var callbacks = [];
        var resolved = false;
        var resolvedCount = 0;
        promises.then = isAllPromisesResolved;
        promises.value.forEach(function (promise) {
            promise.then(isAllPromisesResolved);
        });
        function callCallback() {
            var cb;
            while (cb = callbacks.shift()) {
                cb();
            }
            promises.done = true;
        }
        function isAllPromisesResolved() {
            resolvedCount++;
            if (promises.value.length === resolvedCount) {
                resolved = true;
                callCallback();
            }
        }
        return (promises.all = {
            then: function (cb) {
                callbacks.push(cb);
                if (!promises.value.length || resolved) {
                    callCallback();
                }
                return this;
            }
        });
    };
    FakePromise.prototype.then = function (cb) {
        this.callbacks.push(cb);
        if (this.resolved) {
            this.resolve();
        }
    };
    FakePromise.prototype.resolve = function () {
        this.resolved = true;
        var cb;
        while (cb = this.callbacks.shift()) {
            cb();
        }
    };
    return FakePromise;
}());
var id = 0;
var FakePromises = /*#__PURE__*/ /*#__PURE__*/ /** @class */ (function () {
    function FakePromises() {
        this.value = [];
        this.then = null;
        this.done = false;
        this.all = null;
        this.id = id++;
    }
    FakePromises.prototype.add = function (promise) {
        /* istanbul ignore next */
        if (process.env.NODE_ENV !== 'production') {
            if (this.done) {
                console.error("The FakePromises of '".concat(this.name, "' has done and cannot add new promise. ") +
                    'Maybe it is a bug of IntactReact');
            }
        }
        this.value.push(promise);
        var then = this.then;
        if (then) {
            promise.then(then);
        }
    };
    FakePromises.prototype.reset = function () {
        this.value = [];
        this.then = null;
        this.done = false;
        this.all = null;
    };
    return FakePromises;
}());

var isReact16 = /^16\./.test(version);
var internalInstanceKey;
function precacheFiberNode(node, placeholder) {
    if (!internalInstanceKey) {
        var keys = Object.keys(placeholder);
        internalInstanceKey = keys[0];
    }
    var fiber = placeholder[internalInstanceKey];
    node[internalInstanceKey] = fiber;
    return fiber;
}
var listeningMarker;
function preparePortalMount(dom) {
    if (dom.parentElement !== document.body)
        return;
    var container = document.createComment(containerComment);
    dom.appendChild(container);
    dom[listeningMarker] = null;
    render(createElement('template'), container, function () {
        // maybe we have not rewrite parent element api yet 
        if (!container._realElement) {
            dom.removeChild(this);
        }
        dom.removeChild(container);
    });
}
var bind = Function.prototype.bind;
// excerpt from react definition
var IS_EVENT_HANDLE_NON_MANAGED_NODE = 1;
var IS_NON_DELEGATED = 2;
var PLUGIN_EVENT_SYSTEM = 1; // react16
var connectFiber = false;
if (!isReact16) {
    Function.prototype.bind = function () {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        var obj = args[0], domEventName = args[1], eventSystemFlags = args[2], targetContainer = args[3], nativeEvent = args[4];
        if (obj === null && isString(domEventName) && isNumber(eventSystemFlags) && targetContainer instanceof Element) {
            var isReactListening = false;
            if (!listeningMarker) {
                var keys = Object.keys(targetContainer);
                var key = keys.find(function (key) { return key.startsWith('_reactListening'); });
                if (key) {
                    listeningMarker = key;
                    isReactListening = true;
                }
                else {
                    isReactListening = false;
                }
            }
            else {
                isReactListening = targetContainer[listeningMarker] && nativeEvent instanceof Event;
            }
            if (isReactListening && (eventSystemFlags & IS_EVENT_HANDLE_NON_MANAGED_NODE) === 0 && (eventSystemFlags & IS_NON_DELEGATED) === 0) {
                /**
                 * Because we only add listeners to the react root container, the comment root container in Wrapper will ignore doing this.
                 * But React will check the container (isMatchingRootContainer). it will always return false since the comment parentNode is not
                 * the targetContainer. Therefore, we fake the parentNode by returning the targetContainer to skip the check in React.
                 * Eventually we restore the value after calling the function.
                 */
                var _fn_1 = this;
                var fn = function (name, eventSystemFlags, targetContainer, nativeEvent) {
                    var targetInst = getClosestInstanceFromNode(nativeEvent.target);
                    var commentRoot = getCommentRoot(targetInst);
                    var ret;
                    unstable_batchedUpdates(function () {
                        var containerInfo;
                        if (commentRoot) {
                            containerInfo = commentRoot.stateNode.containerInfo;
                            commentRoot.stateNode.containerInfo = targetContainer;
                            connectFiber = true;
                        }
                        ret = _fn_1(name, eventSystemFlags, targetContainer, nativeEvent);
                        if (commentRoot) {
                            commentRoot.stateNode.containerInfo = containerInfo;
                            connectFiber = false;
                        }
                    });
                    return ret;
                };
                return bind.call(fn, null, domEventName, eventSystemFlags, targetContainer, nativeEvent);
            }
        }
        return bind.call.apply(bind, __spreadArray([this], args, false));
    };
}
else {
    Function.prototype.bind = function () {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        var obj = args[0], domEventName = args[1], eventSystemFlags = args[2], targetContainer = args[3], nativeEvent = args[4];
        if (obj === null &&
            isString(domEventName) &&
            eventSystemFlags === PLUGIN_EVENT_SYSTEM &&
            targetContainer === document &&
            nativeEvent instanceof Event) {
            var _fn_2 = this;
            var fn = function (name, eventSystemFlags, targetContainer, nativeEvent) {
                var ret;
                unstable_batchedUpdates(function () {
                    connectFiber = true;
                    ret = _fn_2(name, eventSystemFlags, targetContainer, nativeEvent);
                    connectFiber = false;
                });
                return ret;
            };
            return bind.call(fn, null, domEventName, eventSystemFlags, targetContainer, nativeEvent);
        }
        return bind.call.apply(bind, __spreadArray([this], args, false));
    };
    /**
     * In React 16, we must let react addEventListener to document firstly.
     * We onl support click here
     */
    var root_1 = document.createElement('div');
    document.body.appendChild(root_1);
    render(createElement('div', { onClick: noop }, 'test'), root_1, function () {
        document.body.removeChild(root_1);
    });
}
function getClosestInstanceFromNode(targetNode) {
    var targetInst = targetNode[internalInstanceKey];
    if (targetInst) {
        // Don't return HostRoot or SuspenseComponent here.
        return targetInst;
    } // If the direct event target isn't a React owned DOM node, we need to look
    // to see if one of its parents is a React owned DOM node.
    var parentNode = targetNode.parentNode;
    while (parentNode) {
        targetInst = parentNode[internalInstanceKey];
        if (targetInst) {
            return targetInst;
        }
        targetNode = parentNode;
        parentNode = targetNode.parentNode;
    }
    return null;
}
function getCommentRoot(targetInst) {
    var node = targetInst;
    while (true) {
        if (node === null)
            return;
        var nodeTag = node.tag;
        if (nodeTag === 3 /* HostRoot */) {
            return node;
        }
        node = node.return;
    }
}
function getReactInternals(component) {
    return component._reactInternals || component._reactInternalFiber;
}

var Context = createContext(null);
var containerComment = ' react-mount-point-unstable ';
var Wrapper = /*#__PURE__*/ /*#__PURE__*/ /** @class */ (function () {
    function Wrapper($props, $vNode, $SVG, $mountedQueue, $senior) {
        this.$props = $props;
        this.$vNode = $vNode;
        this.$SVG = $SVG;
        this.$mountedQueue = $mountedQueue;
        this.$senior = $senior;
        this.$inited = true;
        this.container = document.createComment(containerComment);
        this.isEmptyReactComponent = false;
        this.$lastInput = createCommentVNode('fake');
        // fakeInput.transition = $vNode.transition;
        // fakeInput.transition = {
        // // only need leave hook to prevent Intact from removing the real dom,
        // // because it will be removed by React.
        // leave: noop
        // } as unknown as TransitionHooks;
    }
    Wrapper.prototype.$init = function (props) { };
    Wrapper.prototype.$render = function (lastVNode, vNode, parentDom, anchor) {
        if (lastVNode) {
            removeVNodeDom(lastVNode, parentDom);
        }
        var container = this.container;
        /**
         * React will insert child before container, if container is a comment node.
         * So we should insert the container into the corresponding position
         */
        if (anchor) {
            parentDom.insertBefore(container, anchor);
        }
        else {
            parentDom.appendChild(container);
        }
        var parentComponent = getParent(this);
        rewriteParentElementApi(parentDom, !!parentComponent);
        // we should set dom before rendering the vNode
        // because the real element may not be inserted after rendering
        // and intact need the dom to insertBefore other elements
        // @unit test: insert react array elements before react element in Intact component. ksc-fe/kpc#869
        this.$lastInput.dom = container;
        // enable intact to replace this component
        this.$lastInput.type |= 8192 /* InUse */;
        this.render(vNode, parentDom, parentComponent);
    };
    Wrapper.prototype.$update = function (lastVNode, vNode, parentDom, anchor) {
        this.container._isUpdate = true;
        this.render(vNode, parentDom);
    };
    Wrapper.prototype.$unmount = function (vNode, nextVNode) {
        var _this = this;
        var container = this.container;
        container._isUpdate = false;
        /**
         * If we update intact component lead to this unmountion,
         * the dom will be removed by react immediately,
         * so we set a flag `hasRemoved` to indicate this case.
         * But maybe the wrapped element returns null, and react
         * will not remove the void text node that we created in render method.
         * So if it is in this case, we should remove the void text node manually.
         */
        var dom = this.$lastInput.dom;
        var hasRemoved = false;
        render(null, container, function () {
            hasRemoved = true;
            if (_this.isEmptyReactComponent) {
                container.parentNode.removeChild(dom);
            }
        });
        if (hasRemoved) {
            this.$lastInput.dom = container;
        }
    };
    Wrapper.prototype.render = function (vNode, parentDom, parentComponent) {
        if (parentComponent === void 0) { parentComponent = getParent(this); }
        var parent = this.$senior;
        var instance = this;
        var container = this.container;
        var vnode = getReactElement(vNode);
        /**
         * pass the $senior as value instead of parentComponent
         * because parentComponent is the the toplevel component and
         * not always equal to the $senior
         * e.g. <A><B><div><C /></div></B></A>
         */
        vnode = createElement(Context.Provider, { value: parent }, vnode);
        if (parentComponent) {
            // if the parent component has providers, pass them to subtree
            var providers = parentComponent.$reactProviders;
            providers.forEach(function (value, provider) {
                vnode = createElement(provider, { value: value }, vnode);
            });
        }
        this.isEmptyReactComponent = false;
        var promise = new FakePromise(function (resolve) {
            var callback = function () {
                /**
                 * add dom to the $lastInput for findDomFromVNode
                 * the real dom will be inserted before the container
                 * but the react component may return null,
                 * so we can't get the previousSibling
                 *
                 * We will add the real element to the container
                 * by rewriting the parentNode.insertBefore api
                 *
                 * If we wrap an React component that returns Intact component directly
                 * e.g. <A><Context.Provider><B /><Context.Provider></A>
                 * the realElement will be the <template>
                 */
                var dom = container._realElement;
                if (!dom ||
                    dom.tagName === 'TEMPLATE' &&
                        dom.getAttribute('data-intact-react') !== null) {
                    // maybe this react component return null,
                    // we create a text as the dom
                    dom = document.createTextNode('');
                    parentDom.insertBefore(dom, container);
                    dom._isEmpty = true;
                }
                if (dom._isEmpty) {
                    instance.isEmptyReactComponent = true;
                }
                dom._mountPoint = container;
                instance.$lastInput.dom = dom;
                /**
                 * React find event props until the root fiber return null
                 * If we have event props outeside the root container, it will be ignored.
                 * So we must connect the fiber's return prop to outer element
                 * @UnitTest: outer react element should receive event
                 */
                var rootFiber = container._reactRootContainer._internalRoot.current;
                Object.defineProperty(rootFiber, 'return', {
                    get: function () {
                        // if (connectFiber) debugger;
                        return connectFiber && parentComponent ? getReactInternals(parentComponent) : null;
                    }
                });
                // console.log(rootContainer);
                // console.dir(container);
                // debugger;
                // getRootContainer(parentComponent);
                // Object.defineProperty((container as any)._reactRootContainer._internalRoot, 'containerInfo', {
                // get() {
                // return getRootContainer(parentComponent); 
                // }
                // });
                resolve();
            };
            if (!parentComponent) {
                render(vnode, container, callback);
            }
            else {
                unstable_renderSubtreeIntoContainer(parentComponent, vnode, container, callback);
            }
        });
        // if the promised has be resolved, it indicate that this render is sync.
        // Maybe we call update in intact directly, and it is not in React's render context
        if (!promise.resolved && parentComponent) {
            var promises = parentComponent.$promises;
            // FIXME: Is it appropriate to discard the promise directly?
            // Unit Test: render react component which will update in render phase
            if (!promises.done) {
                promises.add(promise);
            }
        }
    };
    return Wrapper;
}());
function getParent(instance) {
    var $senior = instance.$senior;
    do {
        if (getReactInternals($senior)) {
            return $senior;
        }
    } while ($senior = $senior.$senior);
    // should not hit this
    // maybe call static method to render react element, i.e. Dialog.info();
    /* istanbul ignore next */
    return null;
}
function rewriteParentElementApi(parentElement, preventListener) {
    if (!parentElement._hasRewrite) {
        var removeChild_1 = parentElement.removeChild;
        parentElement.removeChild = function (child, directly) {
            if (child.nodeType === 8 && child.nodeValue === containerComment) {
                removeChild_1.call(parentElement, child._realElement);
                removeChild_1.call(parentElement, child);
                child._realElement = null;
                return;
            }
            var mountPoint = child._mountPoint;
            if (directly || !mountPoint || mountPoint._isUpdate !== false /* maybe react update itself */) {
                removeChild_1.call(parentElement, child);
                return;
            }
            if (!child.parentNode) {
                // if the node has been removed, then remove the mount point
                removeChild_1.call(parentElement, child._mountPoint);
                child._mountPoint = null;
                return;
            }
            if (child._deleted) {
                removeChild_1.call(parentElement, child._mountPoint);
                removeChild_1.call(parentElement, child);
                child._mountPoint = null;
                return;
            }
            child._deleted = true;
            // if (child.parentNode) {
            // removeChild.call(parentElement, child);
            // const realElement = (child as any)._realElement
            // if (realElement) {
            // (child as any)._realElement = null;
            // realElement._mountPoint = null;
            // }
            // } else {
            // if (process.env.NODE_ENV !== 'production') {
            // if (!child._mountPoint) {
            // throw new Error('Cannot remove the node. Maybe it is a bug of intact-react.');
            // }
            // }
            // // if the node has been removed, then remove the mount point
            // const container = child._mountPoint!;
            // removeChild.call(parentElement, container);
            // child._mountPoint = null;
            // (container as any)._realElement = null;
            // }
        };
        var insertBefore_1 = parentElement.insertBefore;
        parentElement.insertBefore = function (child, beforeChild) {
            if (beforeChild.nodeType === 8 && beforeChild.nodeValue === containerComment) {
                // it's the wrapper container, we add the real element to it
                beforeChild._realElement = child;
            }
            insertBefore_1.call(parentElement, child, beforeChild);
        };
        // let react don't add listeners to the root container
        if (preventListener && listeningMarker /* react 16 does not have it */) {
            parentElement[listeningMarker] = true;
        }
        parentElement._hasRewrite = true;
    }
}
function getReactElement(vNode) {
    var props = vNode.props;
    var vnode = props.vnode;
    // react vNode has been frozen, so we must clone it to change
    var _props = null;
    for (var key in props) {
        if (key === 'vnode')
            continue;
        if (!_props)
            _props = {};
        var value = props[key];
        if (key.substr(0, 3) === 'ev-') {
            _props[eventsMap[key]] = value;
        }
        else {
            _props[key] = value;
        }
    }
    return _props ? cloneElement(vnode, _props) : vnode;
}
var eventsMap = {
    'ev-click': 'onClick',
    'ev-contextmenu': 'onContextMenu',
    'ev-mouseenter': 'onMouseEnter',
    'ev-mouseleave': 'onMouseLeave',
    'ev-dragstart': 'onDragStart',
    'ev-dragover': 'onDragOver',
    'ev-dragend': 'onDragEnd',
};

var cid = 'IntactReactFunctional';
function functionalWrapper(Component) {
    function Ctor(props, context) {
        if (context) {
            // invoked by React
            var vNodes = Component(normalizeProps(props, Component.events), true);
            if (isArray(vNodes)) {
                return createElement(Fragment, null, vNodes.map(function (vNode, index) {
                    return normalizeIntactVNodeToReactVNode(vNode, index);
                }));
            }
            return normalizeIntactVNodeToReactVNode(vNodes, 0);
        }
        else {
            // invoked by Intact
            return Component(props);
        }
    }
    var element = forwardRef(function (props, ref) {
        if (ref)
            props = __assign(__assign({}, props), { forwardRef: ref });
        return createElement(Ctor, props);
    });
    element._$type = Ctor;
    element.$cid = cid;
    return element;
}
function normalizeIntactVNodeToReactVNode(vNode, key) {
    var props = vNode.props;
    return createElement(vNode.tag, props ? __assign({ key: key }, props) : { key: key });
}

function normalize(vNode) {
    if (isInvalid(vNode))
        return null;
    // if a element has one child which is string or number
    // intact will set text content directly to update its children
    // this will lead to that the container which return
    // by Wrapper has been removed.
    // so we should convert string or number child
    // to VNode to let intact update it one by one
    if (isStringOrNumber(vNode)) {
        return createTextVNode(vNode);
    }
    // maybe return by functional component, see unit test: `render intact functional component`
    if (isNumber(vNode.type)) {
        return vNode;
    }
    if (isIntactComponent(vNode)) {
        var key = vNode.key;
        var type = vNode.type;
        var tag = type.$cid === cid ? type._$type : type;
        var props = normalizeProps(vNode.props, tag.events);
        if (!isNullOrUndefined(key)) {
            props.key = key;
        }
        return createComponentVNode(4 /* Types.ComponentKnown */, tag, props, key, normalizeRef(vNode.ref));
    }
    if (vNode.type === Fragment) {
        return normalizeChildren(vNode.props.children);
    }
    var ret = createComponentVNode(4, Wrapper, { vnode: vNode }, vNode.key);
    // transition has two functions
    // 1. prevent Intact from removing the real dom,
    //    because it will be removed by React.
    // TODO: 2. let intact never call clearDom method to remove all children
    //    because it will cause `react-mount-point-unstable` to be missing
    // ret.transition = {
    // leave: noop,
    // } as unknown as TransitionHooks;
    return ret;
}
function normalizeChildren(vNodes) {
    if (isArray(vNodes)) {
        var ret_1 = [];
        vNodes.forEach(function (vNode) {
            if (isArray(vNode)) {
                ret_1.push.apply(ret_1, normalizeChildren(vNode));
            }
            else {
                ret_1.push(normalize(vNode));
            }
        });
        return ret_1;
    }
    return normalize(vNodes);
}
function normalizeProps(props, events) {
    // if (!props) return null;
    var blocks = null;
    var normalizedProps = {};
    var tmp;
    var _loop_1 = function (key) {
        var value = props[key];
        if (key === 'children') {
            normalizedProps[key] = normalizeChildren(value);
        }
        else if (tmp = getEventName(key, events)) {
            /**
             * We have to call react event callback asynchronously, because react will update
             * view immediately when one native event happend.
             * @unit test: update in updating
             * @issue: https://github.com/ksc-fe/kpc/issues/894
             *
             * @Modify: nextTick will affect event stopPropagation
             * @issue: #973
             */
            normalizedProps[tmp] = (function () {
                var args = [];
                for (var _i = 0; _i < arguments.length; _i++) {
                    args[_i] = arguments[_i];
                }
                // nextTick(() => {
                unstable_batchedUpdates(function () {
                    value.apply(void 0, args);
                });
                // });
            });
            // normalizedProps[tmp] = value;
        }
        else if (key.startsWith('slot')) {
            if (!blocks)
                blocks = normalizedProps.$blocks = {};
            blocks[hyphenate(key.substring(4))] = normalizeBlock(value);
        }
        else if (key === 'forwardRef') {
            normalizedProps.ref = value;
        }
        else {
            normalizedProps[key] = value;
        }
    };
    for (var key in props) {
        _loop_1(key);
    }
    return normalizedProps;
}
function normalizeRef(ref) {
    return isObject(ref) ? function (i) { ref.current = i; } : ref;
}
function isIntactComponent(vNode) {
    var type = vNode.type;
    return !!type.$cid;
}
function getEventName(propName, events) {
    var third = propName[2];
    if (!third)
        return;
    var first = propName[0];
    var second = propName[1];
    var tmp;
    if (first === 'o' && second === 'n') {
        if (third === '$') {
            // e.g. on$change-value
            return "ev-$".concat(propName.substring(3).replace(/\-/g, ':'));
        }
        else if ((tmp = third.charCodeAt(0)) && tmp >= 65 && tmp <= 90) {
            var eventName = propName.substring(2);
            if (eventName === 'Change') {
                // i.e onChange -> ev-change
                return "ev-change";
            }
            else if (eventName.startsWith('Change')) {
                // e.g. onChangeValue -> ev-$change:value
                return "ev-$change:".concat(lowerFirst(eventName.substring(6)));
            }
            else {
                // e.g. onClick -> ev-click; onKeyPress -> ev-keypress
                var loweredFirst = lowerFirst(eventName.replace(/-/g, ':'));
                if (!events || !events[loweredFirst]) {
                    // is native event
                    loweredFirst = loweredFirst.toLowerCase();
                }
                return "ev-".concat(loweredFirst);
            }
        }
    }
}
function cache(fn) {
    var cache = Object.create(null);
    return function (str) {
        return cache[str] || (cache[str] = fn(str));
    };
}
var lowerFirst = cache(function (str) {
    return str.charAt(0).toLowerCase() + str.slice(1);
});
var hyphenateRE = /\B([A-Z])/g;
var hyphenate = cache(function (str) {
    return (str.charAt(0) + str.slice(1).replace(hyphenateRE, '-$1')).toLowerCase();
});
function normalizeBlock(block) {
    if (isFunction(block)) {
        return function (parent) {
            var args = [];
            for (var _i = 1; _i < arguments.length; _i++) {
                args[_i - 1] = arguments[_i];
            }
            return normalizeChildren(block.apply(void 0, args));
        };
    }
    return function () {
        return normalizeChildren(block);
    };
}

var PROMISES = '_$IntactReactPromises';
var EMPTY_ARRAY = [];
if (process.env.NODE_ENV !== 'production') {
    Object.freeze(EMPTY_ARRAY);
}
var Component = /*#__PURE__*/ /*#__PURE__*/ /** @class */ (function (_super) {
    __extends(Component, _super);
    function Component(props, $vNodeOrContext, $SVG, $mountedQueue, $senior) {
        var _this = this;
        if ($vNodeOrContext && isComponentClass($vNodeOrContext)) {
            // Intact component in intact
            _this = _super.call(this, props, $vNodeOrContext, $SVG, $mountedQueue, $senior) || this;
        }
        else {
            // super(null, null as any, false, [], null);
            // Intact component in React
            // @ts-ignore
            var normalizedProps = normalizeProps(props, _this.constructor.events);
            var parent_1 = $vNodeOrContext;
            var mountedQueue = getMountedQueue(parent_1);
            _this = _super.call(this, normalizedProps, null, false, mountedQueue, parent_1) || this;
            _this.$inited = true;
            // create $vNode
            _this.$vNode = createComponentVNode(4, _this.constructor, normalizedProps);
            if (process.env.NODE_ENV !== 'production') {
                validateProps(_this.$vNode);
            }
            _this.$elementRef = createRef();
            if (_this.constructor.$doubleVNodes) {
                _this.$elementAlternateRef = createRef();
            }
            _this.$isReact = true;
            var promises = _this.$promises = new FakePromises();
            if (process.env.NODE_ENV !== 'production') {
                promises.name = _this.constructor.name;
            }
            _this.$parentPromises = inject(PROMISES, null);
            provide(PROMISES, promises);
        }
        return _this;
    }
    Component.prototype.render = function () {
        var _this = this;
        // save all Context.Proiver, because React will clear the value
        // on commit phase, and we cannot get it when we render react element
        // in Wrapper
        var providers = this.$reactProviders = new Map();
        var returnFiber = getReactInternals(this);
        // let memoizedProps always change to trigger update, see beginWork function in React codes 
        // returnFiber is not extensible
        var memoizedProps;
        var defineProperty = function (obj) {
            Object.defineProperty(obj, 'memoizedProps', {
                get: function () {
                    return memoizedProps;
                },
                set: function (v) {
                    _this.props = memoizedProps = __assign({}, v);
                }
            });
        };
        defineProperty(returnFiber);
        // should also change the alternate's memoizedProps
        var alternate = returnFiber.alternate;
        if (alternate) {
            defineProperty(alternate);
        }
        while (returnFiber = returnFiber.return) {
            var tag = returnFiber.tag;
            if (tag === 10) { // is ContextProiver
                var type = returnFiber.type;
                providers.set(type, type._context._currentValue);
            }
            else if (tag === 3) { // HostRoot
                /**
                 * React will update from root, if root has pendingContext, it will compare
                 * the last value and the current value to change `didPerformWorkStatckCursor`,
                 * if the cursor is true, all children will be updated
                 * @FIXME: Maybe we can use all Consumer to wrap the placeholder element
                 *
                 * always let hasContextChanged return true to make React update the component,
                 * even if it props has not changed
                 * see unit test: `should update children when provider's children...`
                 * issue: https://github.com/ksc-fe/kpc/issues/533
                 *
                 * @Modify: we should not update all from root
                 * https://github.com/ksc-fe/kpc/issues/845
                 **/
                // const stateNode = returnFiber.stateNode;
                // if (!stateNode.__intactReactDefinedProperty) {
                // let context: any;
                // Object.defineProperty(stateNode, 'pendingContext', {
                // get() {
                // return context || (returnFiber.context ? {...returnFiber.context} : Object.create(null));
                // },
                // set(v) {
                // context = v;
                // }
                // });
                // stateNode.__intactReactDefinedProperty = true;
                // }
                break;
            }
        }
        var vNode = createTemplateVNode(this.$elementRef, '1', providers);
        return this.constructor.$doubleVNodes ?
            [vNode, createTemplateVNode(this.$elementAlternateRef, '2', providers)] :
            vNode;
    };
    Component.prototype.componentDidMount = function () {
        var _this = this;
        var vNode = this.$vNode;
        var placeholder = this.$elementRef.current;
        var parentElement = this.$parentElement = placeholder.parentElement;
        // React will assign a emptyObject which is frozen to refs on mountClassInstance
        // we have to re-assign it
        this.refs = {};
        // we may call hooks in init lifecycle, so set instance here,
        // $init method will set it to null
        setInstance(this);
        this.$init(vNode.props);
        vNode.children = this;
        /**
         * let intact element be inserted before placeholder, #834
         */
        // let sibling: any = placeholder.nextSibling;
        // let sibling: any = placeholder;
        // while (sibling = sibling.nextSibling) {
        // if (sibling.nodeType !== 8 || sibling.nodeValue !== containerComment) {
        // break;
        // }
        // }
        this.$render(null, vNode, parentElement, placeholder, this.$mountedQueue);
        this.$done(function () {
            handleElementOnMounted(parentElement, placeholder, findDomFromVNode(vNode, true));
            if (_this.constructor.$doubleVNodes) {
                handleElementOnMounted(parentElement, _this.$elementAlternateRef.current, findDomFromVNode(vNode, false));
            }
        });
    };
    Component.prototype.componentDidUpdate = function () {
        var normalizedProps = normalizeProps(this.props, this.constructor.events);
        var vNode = createComponentVNode(4, this.constructor, normalizedProps);
        if (process.env.NODE_ENV !== 'production') {
            validateProps(vNode);
        }
        vNode.children = this;
        var lastVNode = this.$vNode;
        this.$vNode = vNode;
        var lastElement = findDomFromVNode(vNode, false);
        var promises = this.$promises;
        if (promises.done) {
            promises.reset();
            var mountedQueue = this.$mountedQueue = getMountedQueue(this.$senior);
            this.$update(lastVNode, vNode, this.$parentElement, null, mountedQueue, false);
            this.$done(function () {
                // maybe intact has changed the element
                var element = findDomFromVNode(vNode, false);
                if (lastElement !== element) {
                    updateElementOnUpdated(element, lastElement);
                }
            });
        }
        else {
            this.$update(lastVNode, vNode, this.$parentElement, null, this.$mountedQueue, false);
        }
    };
    Component.prototype.componentWillUnmount = function () {
        this.$unmount(this.$vNode, null);
    };
    Component.prototype.$done = function (callback) {
        var _this = this;
        var done = function () {
            var p = FakePromise.all(_this.$promises);
            if (callback) {
                return p.then(callback);
            }
            return p;
        };
        var $parentPromises = this.$parentPromises;
        if ($parentPromises && !$parentPromises.done) {
            $parentPromises.add(new FakePromise(function (resolve) {
                done().then(resolve);
            }));
        }
        else {
            done().then(function () {
                callMountedQueue(_this.$mountedQueue);
                // callAll(this.$mountedQueue);
            });
        }
    };
    Component.prototype.setState = function () { };
    Component.prototype.$mount = function (lastVNode, nextVNode) {
        _super.prototype.$mount.call(this, lastVNode, nextVNode);
        if (this.$pendingUnmount) {
            this.$pendingUnmount();
            this.$pendingUnmount = undefined;
        }
        if (this.$isPortal) {
            preparePortalMount(findDomFromVNode(this.get('children'), false));
        }
    };
    Component.prototype.$unmount = function (vNode, nextVNode) {
        var _this = this;
        if (!this.$mounted) {
            this.$pendingUnmount = function () { return _super.prototype.$unmount.call(_this, vNode, nextVNode); };
        }
        else {
            _super.prototype.$unmount.call(this, vNode, nextVNode);
        }
    };
    Component.$cid = 'IntactReact';
    Component.normalize = normalizeChildren;
    Component.functionalWrapper = functionalWrapper;
    Component.contextType = Context;
    Component.FakePromise = FakePromise;
    // if the flag is true, it indicate the component returns 2 vNodes,
    // only support two, becasue kpc components don't return vNodes exceed 2.
    Component.$doubleVNodes = false;
    return Component;
}(Component$1));
Component.prototype.isReactComponent = true;
function getMountedQueue(parent) {
    var queue = [];
    queue.priority = [];
    if (parent) {
        var mountedQueue = parent.$mountedQueue;
        var parentPromises = parent.$provides[PROMISES];
        if (!parentPromises.done) {
            var children = mountedQueue.children || (mountedQueue.children = []);
            children.push(queue);
        }
    }
    return queue;
}
function callMountedQueue(mountedQueue) {
    var priority = mountedQueue.priority;
    if (priority) {
        callAll(priority);
    }
    var children = mountedQueue.children;
    if (children) {
        children.forEach(function (mountedQueue) {
            callMountedQueue(mountedQueue);
        });
    }
    callAll(mountedQueue);
}
function createTemplateVNode(ref, key, providers) {
    var vnode = createElement('template', {
        ref: ref,
        key: key,
        'data-intact-react': '',
    });
    providers.forEach(function (value, provider) {
        vnode = createElement(provider._context.Consumer, { key: key }, (function (vnode) {
            return function () { return vnode; };
        })(vnode));
    });
    return vnode;
}
function handleElementOnMounted(parentElement, placeholder, element) {
    parentElement.removeChild(placeholder, true);
    updateElementOnUpdated(element, placeholder);
}
function updateElementOnUpdated(element, placeholder) {
    // replace some properties like React do
    var fiber = precacheFiberNode(element, placeholder);
    // if we return a React element directly, we can not replace its props by placeholder's props,
    // otherwise its event props will be removed
    // updateFiberProps(element, placeholder);
    fiber.stateNode = element;
    var alternate = fiber.alternate;
    if (alternate) {
        alternate.stateNode = element;
    }
}

export { Component, normalizeChildren as normalize };
